'use client'

// The monorail: its beam and pylons along the curve Sam draws in the blend
// (lib/world/monorail.js lays it out, scripts/blender/monorail-sync.py
// brings the curve over). No train yet.
//
// Built here from two small pieces rather than exported as a model, so
// that moving a point of the curve moves the whole line:
//
//   - a piece of track, PIECE long: two concrete beams side by side (Sam,
//     2 Nov: two, like the Tokyo monorail he sent), each with a steel guide
//     rail along both sides and a bracket under it every metre, a lower
//     rail, a cable duct down its outer side, and its joint at the start (a
//     dark gap with a steel plate over it). Straight pieces are instances
//     of it. A piece on a bend is the same geometry bent along the curve,
//     the inner beam on the tighter curve, and the bent ones of a section
//     are merged into one mesh
//   - a pylon, brutalist after the same photos: an eight-sided column, a
//     hammerhead cap under both beams that slopes in to it, and under each
//     beam a concrete seat with a steel bearing on it. One length for all of them, sunk into the
//     ground as far as each one needs, so they can all be instances of one
//     piece
//   - a station wherever the line runs through a building (Sam, 2 Nov, after
//     Chongqing's Liziba: through the flats): the slab the beams rest on, a
//     platform either side with its yellow line, walls with clocks and
//     notice boards, a ceiling of strip lights, name boards, benches,
//     columns, and a frame round each opening. Built along the line, so it
//     moves with it
//
// Where the line goes into a hill (his tunnels in the mountains, off the
// map) the portal and the cutting in front of it are a model, made in the
// blend (MonorailModels, below, with the lift up to the station and its
// ticket machine): the line runs on into the tunnel, going
// dark with its lining, and stops at the black at the back.
//
// Each section of the line is drawn on its own (up to three draws, one
// material, one texture), so a section off screen costs nothing. Today the
// line is one section, three draws and three in the shadow pass, which
// measured cheapest (SECTION_PIECES in lib/world/monorail.js); the station
// is one more draw. The texture is an atlas of twelve of Sam's
// (scripts/monorail-atlas.mjs), and each face says which tile it wears and
// repeats it by the metre, so nothing stretches; the strip lights and name
// boards glow with their own picture.

import { useMemo, useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { texture, uv, attribute, fract, vec2 } from 'three/tsl'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { useModel, useKTX2Texture } from '../hooks/useModel'
import { ditherMaterial } from '../effects/ditherRegistry'
import { useGridMaterial } from '../materials/GridMaterial'
import { layout, PIECE, BEAM, PYLON, STATION, PORTAL, LIFT, STATION_STEPS, stationFurniture } from '../../../lib/world/monorail'
import { liftsNow, allLifts } from '../../../lib/world/liftClient'
import { hearLift } from '../../../lib/audio/lift'
import useGameStore from '../../../stores/gameStore'

// the atlas's tiles (images/monorail_atlas.png, 4 by 3): the offset of
// each, how much it glows, and whether a face wears it once, whole, rather
// than repeated by the metre
const TILE = [1 / 4, 1 / 3]
const at = (c, r, glow = 0, once = false) => [c * TILE[0], r * TILE[1], glow, once]
const STREAKED = at(0, 0)   // the columns, caps, portals, station walls
const CONCRETE = at(1, 0)   // the beams
const STEEL = at(2, 0)      // rails, brackets, the joints' plates, benches
const CHARCOAL = at(3, 0)   // the joints, the bearings, the duct
const TILES = at(0, 1)      // the platforms
const CEILING = at(1, 1)
const YELLOW = at(2, 1)     // the platforms' edges
const LIGHT = at(3, 1, 1.4, true)   // a strip light's tube
const SIGN = at(0, 2, 0.4, true)    // the station's name board, lit a little from behind
const CLOCK = at(1, 2, 0, true)
const BOARD = at(2, 2, 0, true)     // a notice board
// metres a tile covers before it repeats
const REPEAT = 3
// pieces bend along the curve at this many points a piece
const BEND_STEPS = 12

// the beam's furniture, in metres from its top (y) and its side (out)
const UPPER = { top: -0.3, bottom: -0.42, out: 0.07 }
const LOWER = { top: -1.04, bottom: -1.12, out: 0.05 }
const BRACKET = { drop: 0.16, length: 0.1, every: 1 }
const DUCT = { top: -0.76, bottom: -0.86, out: 0.1 }
const JOINT = 0.1

// One flat quad. Its corners may come in either order: it is turned to
// face `out`, so each shape below only says which way each face looks.
function quad(o, a, b, c, d, uvs, tile, out) {
  const e1 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], e2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]]
  let n = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]]
  if (n[0] * out[0] + n[1] * out[1] + n[2] * out[2] < 0) { [b, d] = [d, b]; uvs = [uvs[0], uvs[3], uvs[2], uvs[1]]; n = n.map((v) => -v) }
  const l = Math.hypot(n[0], n[1], n[2]) || 1
  const base = o.pos.length / 3
  for (const p of [a, b, c, d]) o.pos.push(p[0], p[1], p[2])
  for (let k = 0; k < 4; k++) { o.nor.push(n[0] / l, n[1] / l, n[2] / l); o.tile.push(tile[0], tile[1]); o.glow.push(tile[2] || 0); o.shade.push(1) }
  if (tile[3]) {
    // worn once: the face's own uvs stretched to the whole tile, just inside it
    const us = uvs.map((t) => t[0]), vs = uvs.map((t) => t[1])
    const u0 = Math.min(...us), u1 = Math.max(...us), v0 = Math.min(...vs), v1 = Math.max(...vs)
    for (const t of uvs) o.uv.push(((t[0] - u0) / (u1 - u0 || 1)) * 0.998 + 0.001, ((t[1] - v0) / (v1 - v0 || 1)) * 0.998 + 0.001)
  } else for (const t of uvs) o.uv.push(t[0] / REPEAT, t[1] / REPEAT)
  o.idx.push(base, base + 1, base + 2, base, base + 2, base + 3)
}

// A box along x, its long faces cut into steps so it can bend. faces names
// a tile for each face that shows (top, bottom, left: +z, right: -z, start:
// -x, end: +x), and leaves out the ones that are hidden. v runs down the
// sides from y = 0, so the streaks run down the concrete.
function box(o, x0, x1, y0, y1, z0, z1, faces, steps = 1) {
  const { top, bottom, left, right, start, end } = faces
  for (let k = 0; k < steps; k++) {
    const a = x0 + ((x1 - x0) * k) / steps, b = x0 + ((x1 - x0) * (k + 1)) / steps
    if (top) quad(o, [a, y1, z0], [a, y1, z1], [b, y1, z1], [b, y1, z0], [[a, z0], [a, z1], [b, z1], [b, z0]], top, [0, 1, 0])
    if (bottom) quad(o, [a, y0, z0], [b, y0, z0], [b, y0, z1], [a, y0, z1], [[a, z0], [b, z0], [b, z1], [a, z1]], bottom, [0, -1, 0])
    if (left) quad(o, [a, y1, z1], [a, y0, z1], [b, y0, z1], [b, y1, z1], [[a, -y1], [a, -y0], [b, -y0], [b, -y1]], left, [0, 0, 1])
    if (right) quad(o, [b, y1, z0], [b, y0, z0], [a, y0, z0], [a, y1, z0], [[-b, -y1], [-b, -y0], [-a, -y0], [-a, -y1]], right, [0, 0, -1])
  }
  if (start) quad(o, [x0, y1, z1], [x0, y1, z0], [x0, y0, z0], [x0, y0, z1], [[z1, -y1], [z0, -y1], [z0, -y0], [z1, -y0]], start, [-1, 0, 0])
  if (end) quad(o, [x1, y1, z0], [x1, y1, z1], [x1, y0, z1], [x1, y0, z0], [[-z0, -y1], [-z1, -y1], [-z1, -y0], [-z0, -y0]], end, [1, 0, 0])
}

function geometry(build) {
  const o = { pos: [], nor: [], uv: [], tile: [], glow: [], shade: [], idx: [] }
  build(o)
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(o.pos, 3))
  g.setAttribute('normal', new THREE.Float32BufferAttribute(o.nor, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(o.uv, 2))
  g.setAttribute('tile', new THREE.Float32BufferAttribute(o.tile, 2))
  g.setAttribute('glow', new THREE.Float32BufferAttribute(o.glow, 1))
  g.setAttribute('shade', new THREE.Float32BufferAttribute(o.shade, 1))
  g.setIndex(o.idx)
  return g
}

// x runs along the line from the joint, y up from the top of the beams
// (the curve), z across it. The beams' own ends are never seen: each piece
// starts inside the joint the one before it ends against.
const BEAMS = [BEAM.gauge / 2, -BEAM.gauge / 2]

const beamPiece = (steps) => geometry((o) => {
  const w = BEAM.width / 2, h = BEAM.height
  for (const c of BEAMS) {
    box(o, 0, PIECE, -h, 0, c - w, c + w, { top: CONCRETE, bottom: CONCRETE, left: CONCRETE, right: CONCRETE }, steps)
    for (const side of [1, -1]) {
      const z = (out) => (side > 0 ? [c + w, c + w + out] : [c - w - out, c - w])
      const faces = (t) => ({ top: t, bottom: t, ...(side > 0 ? { left: t } : { right: t }) })
      // the guide rails the train's wheels will run on, steel, both sides
      box(o, 0, PIECE, UPPER.bottom, UPPER.top, ...z(UPPER.out), faces(STEEL), steps)
      box(o, 0, PIECE, LOWER.bottom, LOWER.top, ...z(LOWER.out), faces(STEEL), steps)
      // a bracket under the upper rail every metre
      for (let x = BRACKET.every / 2; x < PIECE; x += BRACKET.every) {
        box(o, x - BRACKET.length / 2, x + BRACKET.length / 2, UPPER.bottom - BRACKET.drop, UPPER.bottom, ...z(UPPER.out - 0.01),
          { bottom: STEEL, ...(side > 0 ? { left: STEEL } : { right: STEEL }), start: STEEL, end: STEEL })
      }
    }
    // the cable duct, down the beam's outer side
    const out = Math.sign(c)
    box(o, 0, PIECE, DUCT.bottom, DUCT.top, ...(out > 0 ? [c + w, c + w + DUCT.out] : [c - w - DUCT.out, c - w]),
      { top: CHARCOAL, bottom: CHARCOAL, ...(out > 0 ? { left: CHARCOAL } : { right: CHARCOAL }) }, steps)
    // the joint: a dark gap round the beam, and a steel plate across its top
    box(o, 0, JOINT, -h - 0.01, 0.004, c - w - 0.01, c + w + 0.01, { top: CHARCOAL, bottom: CHARCOAL, left: CHARCOAL, right: CHARCOAL, start: CHARCOAL, end: CHARCOAL })
    box(o, -0.15, 0.25, 0.004, 0.02, c - 0.36, c + 0.36, { top: STEEL, left: STEEL, right: STEEL, start: STEEL, end: STEEL })
  }
})

// an open line's end: a concrete face on each beam, with its joint, which no piece after it brings
const lineEnd = () => geometry((o) => {
  const w = BEAM.width / 2, h = BEAM.height
  for (const c of BEAMS) {
    box(o, -JOINT, 0, -h - 0.01, 0.004, c - w - 0.01, c + w + 0.01, { top: CHARCOAL, bottom: CHARCOAL, left: CHARCOAL, right: CHARCOAL, start: CHARCOAL, end: STREAKED })
  }
})

// y = 0 is the underside of the beams, x along them. The column is as long
// as the tallest pylon needs; the rest of it is under the ground.
const pylonPiece = (column) => geometry((o) => {
  const { bearing, seat, capTop, capBottom, capLength, capHeight, capEdge, radius } = PYLON
  for (const c of BEAMS) {
    // the bearing each beam rests on, and the seat under it
    // the seat stands out past the beam's rails and duct, so it reads as what the beam sits on
    box(o, -0.45, 0.45, -bearing, 0, c - 0.4, c + 0.4, { left: CHARCOAL, right: CHARCOAL, start: CHARCOAL, end: CHARCOAL })
    box(o, -0.7, 0.7, -bearing - seat, -bearing, c - 0.72, c + 0.72, { top: STREAKED, left: STREAKED, right: STREAKED, start: STREAKED, end: STREAKED })
  }
  // the cap: across the line it is square at the ends for capEdge, then
  // slopes in to a soffit a little wider than the column
  const yt = -bearing - seat, ye = yt - capEdge, yb = yt - capHeight
  const x0 = -capLength / 2, x1 = capLength / 2, T = capTop / 2, B = capBottom / 2
  const slope = Math.hypot(T - B, ye - yb)
  quad(o, [x0, yt, -T], [x0, yt, T], [x1, yt, T], [x1, yt, -T], [[x0, -T], [x0, T], [x1, T], [x1, -T]], STREAKED, [0, 1, 0])
  quad(o, [x0, yb, -B], [x1, yb, -B], [x1, yb, B], [x0, yb, B], [[x0, -B], [x1, -B], [x1, B], [x0, B]], STREAKED, [0, -1, 0])
  for (const s of [1, -1]) {
    quad(o, [x0, yt, s * T], [x0, ye, s * T], [x1, ye, s * T], [x1, yt, s * T], [[x0, -yt], [x0, -ye], [x1, -ye], [x1, -yt]], STREAKED, [0, 0, s])
    quad(o, [x0, ye, s * T], [x0, yb, s * B], [x1, yb, s * B], [x1, ye, s * T], [[x0, -ye], [x0, -ye + slope], [x1, -ye + slope], [x1, -ye]], STREAKED, [0, -0.6, s])
    // its two faces along the line: the square top, then the haunches
    const xe = s > 0 ? x1 : x0
    quad(o, [xe, yt, -T], [xe, yt, T], [xe, ye, T], [xe, ye, -T], [[-T, -yt], [T, -yt], [T, -ye], [-T, -ye]], STREAKED, [s, 0, 0])
    quad(o, [xe, ye, -T], [xe, ye, T], [xe, yb, B], [xe, yb, -B], [[-T, -ye], [T, -ye], [B, -yb], [-B, -yb]], STREAKED, [s, 0, 0])
  }
  // the column, eight flat faces, square to the line
  const ya = yb, yz = yb - column, side = 2 * radius * Math.tan(Math.PI / 8)
  for (let k = 0; k < 8; k++) {
    const a0 = Math.PI / 8 + (k * Math.PI) / 4, a1 = a0 + Math.PI / 4, am = (a0 + a1) / 2
    const p0 = [Math.cos(a0) * radius / Math.cos(Math.PI / 8), Math.sin(a0) * radius / Math.cos(Math.PI / 8)]
    const p1 = [Math.cos(a1) * radius / Math.cos(Math.PI / 8), Math.sin(a1) * radius / Math.cos(Math.PI / 8)]
    const u0 = k * side, u1 = (k + 1) * side
    quad(o, [p0[0], ya, p0[1]], [p0[0], yz, p0[1]], [p1[0], yz, p1[1]], [p1[0], ya, p1[1]], [[u0, -ya], [u0, -yz], [u1, -yz], [u1, -ya]], STREAKED, [Math.cos(am), 0, Math.sin(am)])
  }
})

// A station, len long: x runs along the line from where it goes into the
// building to where it comes out, y up from the beams' tops, z across. The
// beams run through it as anywhere else; this is everything round them.
const stationPiece = (len) => geometry((o) => {
  const { half, wall, below, platform, inner, ceiling, above, frame } = STATION
  const h = BEAM.height
  // the building's floor, which the beams rest on, and its ceiling over everything
  box(o, 0, len, -h - below, -h, -half, half, { top: CONCRETE, start: STREAKED, end: STREAKED })
  box(o, 0, len, ceiling, above, -half, half, { bottom: CEILING, start: STREAKED, end: STREAKED })
  for (const side of [1, -1]) {
    const zz = (a, b) => (side > 0 ? [a, b] : [-b, -a])
    const toward = (t) => (side > 0 ? { right: t } : { left: t })   // the face looking at the line
    const away = (t) => (side > 0 ? { left: t } : { right: t })
    const back = half - wall
    // the platform, up to a train's floor, its yellow line along the edge
    box(o, 0, len, -h, platform, ...zz(inner, back), { top: TILES, ...toward(STREAKED), start: STREAKED, end: STREAKED })
    box(o, 0, len, platform, platform + 0.01, ...zz(inner, inner + 0.3), { top: YELLOW })
    // the wall behind it
    box(o, 0, len, platform, ceiling, ...zz(back, half), { ...toward(STREAKED), start: STREAKED, end: STREAKED })
    // strip lights down the platform and over the line
    for (let x = 1; x + 1.8 < len; x += 3) {
      box(o, x, x + 1.8, ceiling - 0.05, ceiling, ...zz(4.7, 5.2), { bottom: LIGHT, start: STEEL, end: STEEL, ...toward(STEEL), ...away(STEEL) })
      if (side > 0) box(o, x, x + 1.8, ceiling - 0.05, ceiling, -0.25, 0.25, { bottom: LIGHT, start: STEEL, end: STEEL, left: STEEL, right: STEEL })
    }
    // columns against the wall, benches between them (stationFurniture, which the walls read too)
    const { columns, benches } = stationFurniture(len)
    for (const [a, b] of columns) box(o, a, b, platform, ceiling, ...zz(back - 0.6, back), { ...toward(STREAKED), start: STREAKED, end: STREAKED })
    for (const [a, b] of benches) box(o, a, b, platform, platform + 0.45, ...zz(back - 1.1, back - 0.6), { top: STEEL, ...toward(STEEL), start: STEEL, end: STEEL })
    // gates along the edge, a steel post every two metres and dark panels
    // between, stopping short of the end the line comes in at, where steel
    // steps go down to the beam (Sam, 3 Nov: the line is "colision objects
    // you can walk along")
    const { gap, from, to, treads } = STATION_STEPS
    for (let x = gap; x < len - 0.05; x += 2) {
      box(o, x - 0.05, x + 0.05, platform, platform + 1.3, ...zz(inner, inner + 0.12), { top: STEEL, ...toward(STEEL), ...away(STEEL), start: STEEL, end: STEEL })
      const x1 = Math.min(len, x + 2)
      if (x1 - x > 0.2) box(o, x + 0.05, x1 - 0.05, platform + 0.12, platform + 1.18, ...zz(inner + 0.03, inner + 0.09), { ...toward(CHARCOAL), ...away(CHARCOAL) })
    }
    box(o, gap, len, platform + 1.22, platform + 1.3, ...zz(inner - 0.01, inner + 0.13), { top: STEEL, bottom: STEEL, ...toward(STEEL), ...away(STEEL), start: STEEL, end: STEEL })
    const run = (to - from) / treads, beamOut = BEAM.gauge / 2 + BEAM.width / 2
    for (let k = 0; k < treads; k++) {
      const y = platform - (platform * (treads - k)) / (treads + 1)
      box(o, from + run * k, from + run * (k + 1), y - 0.06, y, ...zz(beamOut, inner), { top: STEEL, bottom: CHARCOAL, ...toward(STEEL), ...away(STEEL), start: STEEL, end: STEEL })
    }
    box(o, from, to, -0.3, platform - 0.1, ...zz(inner - 0.12, inner - 0.04), { ...toward(CHARCOAL), ...away(CHARCOAL), start: CHARCOAL, end: CHARCOAL })
    // the station's name, hung over the platform both ways
    for (let x = len / 2 - 9; x < len; x += 9) {
      if (x - 1.2 < 0.5) continue
      box(o, x - 1.2, x + 1.2, ceiling - 1.0, ceiling - 0.4, ...zz(4.4, 4.48), { left: SIGN, right: SIGN, top: STEEL, bottom: STEEL, start: STEEL, end: STEEL })
      for (const dx of [-1, 1]) box(o, x + dx - 0.02, x + dx + 0.02, ceiling - 0.4, ceiling, ...zz(4.42, 4.46), { start: STEEL, end: STEEL, left: STEEL, right: STEEL })
    }
    // a clock in the middle of the wall, notice boards either side of it
    box(o, len / 2 - 0.4, len / 2 + 0.4, 2.6, 3.4, ...zz(back - 0.05, back), { ...toward(CLOCK), start: STEEL, end: STEEL, top: STEEL, bottom: STEEL })
    for (const f of [0.25, 0.75]) box(o, len * f - 0.7, len * f + 0.7, 1.7, 2.7, ...zz(back - 0.04, back), { ...toward(BOARD), start: STEEL, end: STEEL, top: STEEL, bottom: STEEL })
    // the side of the frame round each opening, standing out from the building
    for (const [a, b] of [[-0.4, 0], [len, len + 0.4]]) {
      box(o, a, b, -h - below - frame, above + frame, ...zz(half, half + frame), { ...toward(STREAKED), ...away(STREAKED), start: STREAKED, end: STREAKED })
    }
  }
  // the frame's top and bottom
  for (const [a, b] of [[-0.4, 0], [len, len + 0.4]]) {
    box(o, a, b, above, above + frame, -half, half, { top: STREAKED, bottom: STREAKED, start: STREAKED, end: STREAKED })
    box(o, a, b, -h - below - frame, -h - below, -half, half, { top: STREAKED, bottom: STREAKED, start: STREAKED, end: STREAKED })
  }
})

// A piece laid along the line from s0: x along it (dir 1 on, -1 back), y up,
// z across, turned with x so the piece is never mirrored.
function place(template, line, s0, dir = 1) {
  const g = template.clone()
  const pos = g.attributes.position, nor = g.attributes.normal
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i) * dir
    const f = line.frame(s0 + x * dir)
    pos.setXYZ(i,
      f.p[0] + f.up[0] * y + f.side[0] * z,
      f.p[1] + f.up[1] * y + f.side[1] * z,
      f.p[2] + f.up[2] * y + f.side[2] * z)
    const nx = nor.getX(i) * dir, ny = nor.getY(i), nz = nor.getZ(i) * dir
    nor.setXYZ(i,
      f.t[0] * nx + f.up[0] * ny + f.side[0] * nz,
      f.t[1] * nx + f.up[1] * ny + f.side[1] * nz,
      f.t[2] * nx + f.up[2] * ny + f.side[2] * nz)
  }
  return g
}

// A piece bent along the curve: each vertex goes to the frame of the curve
// at its distance along the piece (x, stretched by k to the piece's length).
function bend(template, line, s0, k) {
  const g = template.clone()
  const pos = g.attributes.position, nor = g.attributes.normal
  g.userData.x = Float32Array.from({ length: pos.count }, (_, i) => pos.getX(i))
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i)
    const f = line.frame(s0 + x * k)
    pos.setXYZ(i,
      f.p[0] + f.up[0] * y + f.side[0] * z,
      f.p[1] + f.up[1] * y + f.side[1] * z,
      f.p[2] + f.up[2] * y + f.side[2] * z)
    const nx = nor.getX(i), ny = nor.getY(i), nz = nor.getZ(i)
    nor.setXYZ(i,
      f.t[0] * nx + f.up[0] * ny + f.side[0] * nz,
      f.t[1] * nx + f.up[1] * ny + f.side[1] * nz,
      f.t[2] * nx + f.up[2] * ny + f.side[2] * nz)
  }
  return g
}

// Track inside a tunnel goes dark as the lining does (monorail-portal.py):
// full light to fadeStart in, nothing by fadeEnd.
function darken(g, tunnel, line, s0, k) {
  const pos = g.attributes.position, shade = g.attributes.shade
  const template = g.userData.x
  for (let i = 0; i < shade.count; i++) {
    const s = s0 + template[i] * k
    const into = (s - tunnel.s) * tunnel.into
    const f = Math.max(0, Math.min(1, 1 - (into - PORTAL.fadeStart) / (PORTAL.fadeEnd - PORTAL.fadeStart)))
    shade.setX(i, f ** 1.6)
  }
  return g
}

// A straight piece: from one end of its stretch to the other, the chord.
const _m = new THREE.Matrix4()
function chordMatrix(line, s0, s1) {
  const a = line.at(s0), b = line.at(s1)
  const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]]
  const len = Math.hypot(d[0], d[1], d[2]) || 1
  const t = [d[0] / len, d[1] / len, d[2] / len]
  let side = [-t[2], 0, t[0]]
  const sl = Math.hypot(side[0], side[2]) || 1
  side = [side[0] / sl, 0, side[2] / sl]
  const up = [side[1] * t[2] - side[2] * t[1], side[2] * t[0] - side[0] * t[2], side[0] * t[1] - side[1] * t[0]]
  const k = len / PIECE
  return _m.set(
    t[0] * k, up[0], side[0], a[0],
    t[1] * k, up[1], side[1], a[1],
    t[2] * k, up[2], side[2], a[2],
    0, 0, 0, 1)
}

function instanced(geo, mat, matrices) {
  const im = new THREE.InstancedMesh(geo, mat, matrices.length)
  matrices.forEach((m, i) => im.setMatrixAt(i, m))
  im.instanceMatrix.needsUpdate = true
  // its own bounds, so a section off screen is culled whole
  im.computeBoundingSphere()
  im.computeBoundingBox()
  im.castShadow = true
  im.receiveShadow = true
  return im
}

export function Monorail() {
  const atlas = useKTX2Texture('/images/monorail_atlas.ktx2')

  const material = useMemo(() => {
    atlas.magFilter = THREE.NearestFilter
    atlas.minFilter = THREE.NearestFilter
    atlas.needsUpdate = true
    const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0 })
    const tex = texture(atlas, attribute('tile', 'vec2').add(fract(uv()).mul(vec2(TILE[0], TILE[1]))))
    // shade: 1 out in the light, falling to 0 inside a tunnel
    mat.colorNode = tex.mul(attribute('shade', 'float'))
    // the strip lights and the name boards light themselves, day and night
    mat.emissiveNode = tex.rgb.mul(attribute('glow', 'float'))
    ditherMaterial(mat)
    return mat
  }, [atlas])

  const built = useMemo(() => {
    const { lines } = layout()
    const group = new THREE.Group()
    group.name = 'monorail'
    const owned = []
    let tallest = 0
    for (const { sections } of lines) for (const sec of sections) for (const p of sec.pylons) tallest = Math.max(tallest, p.top - BEAM.height - p.ground)
    const straightPiece = beamPiece(1)
    const bentPiece = beamPiece(BEND_STEPS)
    const end = lineEnd()
    const pylon = pylonPiece(Math.max(0, tallest - PYLON.bearing - PYLON.seat - PYLON.capHeight) + 0.5)
    owned.push(straightPiece, bentPiece, end, pylon)
    const stats = { sections: 0, straight: 0, bent: 0, pylons: 0, draws: 0, triangles: 0 }
    const tris = (g) => g.index.count / 3

    // the stations of every line, one mesh
    const extras = []
    lines.forEach(({ line, stations }) => {
      for (const st of stations) {
        const t = stationPiece(st.s1 - st.s0)
        extras.push(place(t, line, st.s0, 1)); t.dispose()
      }
    })
    if (extras.length) {
      const merged = mergeGeometries(extras, false)
      extras.forEach((b) => b.dispose())
      if (merged) {
        owned.push(merged)
        const mesh = new THREE.Mesh(merged, material)
        mesh.name = 'monorail_stations'
        mesh.castShadow = true
        mesh.receiveShadow = true
        group.add(mesh)
        stats.draws++
        stats.triangles += tris(merged)
      }
    }

    lines.forEach(({ line, sections }, li) => {
      sections.forEach((sec, si) => {
        const g = new THREE.Group()
        g.name = `monorail_${li}_${si}`
        const straights = [], bends = []
        for (const p of sec.pieces) {
          if (p.end) bends.push(bend(end, line, p.s0, 1))
          else if (p.inside) bends.push(darken(bend(bentPiece, line, p.s0, (p.s1 - p.s0) / PIECE), p.inside, line, p.s0, (p.s1 - p.s0) / PIECE))
          else if (p.straight) straights.push(chordMatrix(line, p.s0, p.s1).clone())
          else bends.push(bend(bentPiece, line, p.s0, (p.s1 - p.s0) / PIECE))
        }
        if (straights.length) {
          g.add(instanced(straightPiece, material, straights))
          stats.triangles += tris(straightPiece) * straights.length
        }
        if (bends.length) {
          const merged = mergeGeometries(bends, false)
          bends.forEach((b) => b.dispose())
          if (merged) {
            owned.push(merged)
            const mesh = new THREE.Mesh(merged, material)
            mesh.castShadow = true
            mesh.receiveShadow = true
            g.add(mesh)
            stats.triangles += tris(merged)
          }
        }
        if (sec.pylons.length) {
          g.add(instanced(pylon, material, sec.pylons.map((p) => {
            const m = new THREE.Matrix4().makeRotationY(p.yaw)
            m.setPosition(p.x, p.top - BEAM.height, p.z)
            return m
          })))
          stats.triangles += tris(pylon) * sec.pylons.length
        }
        stats.sections++
        stats.straight += straights.length
        stats.bent += sec.pieces.filter((p) => !p.end).length - straights.length
        stats.pylons += sec.pylons.length
        stats.draws += g.children.length
        group.add(g)
      })
    })
    group.userData.stats = stats
    return { group, owned }
  }, [material])

  useEffect(() => () => {
    built.owned.forEach((g) => g.dispose())
    built.group.traverse((o) => { if (o.isInstancedMesh) o.dispose() })
  }, [built])

  useEffect(() => () => material.dispose(), [material])

  return (
    <>
      <primitive object={built.group} />
      <MonorailModels />
    </>
  )
}

// What is modelled in the blend and placed by the sync
// (public/models/monorailModels.glb, world coordinates baked): the portals
// and their cuttings, the lift tower with its car and doors, and the ticket
// machine. Sam's streaked concrete and the taco pack's steel, toned by
// vertex colour; the signs (scripts/monorail-signs.mjs) wear
// 'monorail_signs', or 'monorail_lit' where they glow. The slab over each
// tunnel wears 'monorail_ground', and is drawn as the ground is, so the
// hill runs over it. Materials made once, here, so the first frame draws
// the final ones. The lift's car and doors move with the town's lift
// (lib/world/liftClient.js); the old tickets round the machine are drawn
// from the machine's state (Tickets, below).
function MonorailModels() {
  const { scene } = useModel('/models/monorailModels.glb')
  const { gridColorNode } = useGridMaterial()
  const parts = useMemo(() => {
    // the loaded scene is cached and shared: made over once, then kept
    if (scene.userData.monorailParts) return scene.userData.monorailParts
    const made = new Map()
    scene.traverse((o) => {
      if (!o.isMesh) return
      o.castShadow = true
      o.receiveShadow = true
      const src = o.material
      if (!src || src.isNodeMaterial) return
      let mat = made.get(src)
      if (!mat && src.name === 'monorail_ground') {
        mat = new THREE.MeshStandardNodeMaterial({ colorNode: gridColorNode, roughness: 1, metalness: 0 })
        ditherMaterial(mat)
        made.set(src, mat)
      }
      if (!mat) {
        // a sign that glows draws its own picture whatever the light
        const lit = src.name.startsWith('monorail_lit')
        mat = lit ? new THREE.MeshBasicNodeMaterial({ vertexColors: true }) : new THREE.MeshStandardNodeMaterial({ roughness: 1, metalness: 0, vertexColors: true })
        if (src.map) {
          src.map.magFilter = THREE.NearestFilter
          src.map.minFilter = THREE.NearestFilter
          src.map.needsUpdate = true
          mat.map = src.map
        }
        ditherMaterial(mat)
        made.set(src, mat)
      }
      o.material = mat
    })
    scene.updateMatrixWorld(true)
    // each lift's moving parts: the car where it stands at the bottom, and
    // its four door leaves, shut, drawn as one instanced mesh (one draw, not four)
    const lifts = allLifts().map((lf) => {
      const car = scene.getObjectByName(`monorail_lift_${lf.i}_car`)
      const leaves = ['low_a', 'low_b', 'high_a', 'high_b'].map((k) => scene.getObjectByName(`monorail_lift_${lf.i}_door_${k}`)).filter((n) => n?.isMesh)
      return { lf, car: car ? { n: car, y: car.position.y } : null, leaves }
    })
    const moving = new Set(lifts.flatMap((l) => [l.car?.n, ...l.leaves]).filter(Boolean))
    const movingPart = (o) => { for (let a = o; a; a = a.parent) if (moving.has(a)) return true; return false }
    let doors = null
    const allLeaves = lifts.flatMap((l) => l.leaves)
    if (allLeaves.length) {
      const g = allLeaves[0].geometry
      doors = new THREE.InstancedMesh(g, allLeaves[0].material, allLeaves.length)
      doors.castShadow = doors.receiveShadow = true
      doors.frustumCulled = false
      // each leaf's own place, stretched to its own length (the top door's are wider)
      g.computeBoundingBox()
      const len0 = g.boundingBox.max.x - g.boundingBox.min.x
      allLeaves.forEach((n, i) => {
        n.geometry.computeBoundingBox()
        const len = n.geometry.boundingBox.max.x - n.geometry.boundingBox.min.x
        n.userData.base = n.matrixWorld.clone().multiply(new THREE.Matrix4().makeScale(len / len0, 1, 1))
        doors.setMatrixAt(i, n.userData.base)
      })
      for (const n of allLeaves) n.removeFromParent()
      scene.add(doors)
    }
    // everything that never moves, merged into one mesh a material: the
    // portals, the cuttings, the towers, the machines. The shadow pass draws
    // all of it every frame wherever the camera looks (docs/monorail.md), so
    // a draw a material costs less than culling them one by one would save
    const byMat = new Map()
    scene.traverse((o) => { if (o.isMesh && o !== doors && !movingPart(o)) (byMat.get(o.material) || byMat.set(o.material, []).get(o.material)).push(o) })
    for (const [mat, meshes] of byMat) {
      if (meshes.length < 2) continue
      const geos = meshes.map((m) => m.geometry.clone().applyMatrix4(m.matrixWorld))
      const merged = mergeGeometries(geos, false)
      geos.forEach((g) => g.dispose())
      if (!merged) continue
      for (const m of meshes) m.removeFromParent()
      const one = new THREE.Mesh(merged, mat)
      one.castShadow = one.receiveShadow = true
      scene.add(one)
    }
    // named, so a measurement can switch it off with the line (scripts/perf)
    scene.name = 'monorail_models'
    scene.userData.monorailParts = { lifts, doors }
    return scene.userData.monorailParts
  }, [scene, gridColorNode])

  const m4 = useMemo(() => new THREE.Matrix4(), [])
  useFrame(({ camera }, dt) => {
    const now = liftsNow()
    let i = 0
    parts.lifts.forEach(({ lf, car, leaves }, k) => {
      const l = now[k]
      if (!l) return
      if (car) car.n.position.y = car.y + (l.y - lf.low)
      // and heard: the motor, the doors, the chime, from where the car is (lib/audio/lift.js)
      hearLift(lf.i, l, lf.car, camera.position, dt)
      // the leaves part along the wall each door is in: the bottom's along the line, the top's across it
      leaves.forEach((n, j) => {
        const high = j >= 2
        const dir = high ? lf.D : lf.U
        const s = (j % 2 ? 1 : -1) * (high ? l.doors[1] : l.doors[0]) * ((high ? LIFT.highDoor : LIFT.lowDoor) / 2)
        m4.makeTranslation(dir[0] * s, 0, dir[1] * s).multiply(n.userData.base)
        parts.doors.setMatrixAt(i++, m4)
      })
    })
    if (parts.doors) parts.doors.instanceMatrix.needsUpdate = true
  })

  return (
    <>
      <primitive object={scene} />
      <Tickets />
    </>
  )
}

// The old used tickets lying round the ticket machine (Sam, 3 Nov: "random
// old used tickets show up around it for him to collect"): where the town
// put each, on the machine's entity, flat on the platform, a little card of
// its own colour. One draw for all of them.
const TICKET_MAX = 16
function Tickets() {
  const mesh = useRef(null)
  const lf = allLifts()[0]
  const geo = useMemo(() => new THREE.PlaneGeometry(0.17, 0.09).rotateX(-Math.PI / 2), [])
  const mat = useMemo(() => { const m = new THREE.MeshStandardNodeMaterial({ roughness: 1, metalness: 0 }); ditherMaterial(m); return m }, [])
  useEffect(() => () => { geo.dispose(); mat.dispose() }, [geo, mat])
  useEffect(() => {
    if (!lf) return
    const m = mesh.current
    const matrix = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), one = new THREE.Vector3(1, 1, 1), colour = new THREE.Color()
    let last = ''
    const draw = (entities) => {
      const tickets = entities.find((e) => e.id === 'activity_ticket_machine')?.state?.tickets || []
      const key = JSON.stringify(tickets)
      if (key === last || !m) return
      last = key
      const shown = tickets.slice(0, TICKET_MAX)
      shown.forEach((t, i) => {
        q.setFromAxisAngle(up, t.yaw || 0)
        matrix.compose(new THREE.Vector3(t.x, lf.high + 0.004 + i * 0.0005, t.z), q, one)
        m.setMatrixAt(i, matrix)
        m.setColorAt(i, colour.set(t.colour || '#d8cfb8'))
      })
      m.count = shown.length
      m.instanceMatrix.needsUpdate = true
      if (m.instanceColor) m.instanceColor.needsUpdate = true
    }
    draw(useGameStore.getState().entityStates)
    return useGameStore.subscribe((s) => draw(s.entityStates))
  }, [lf])
  if (!lf) return null
  return <instancedMesh ref={mesh} args={[geo, mat, TICKET_MAX]} count={0} frustumCulled={false} receiveShadow />
}

export default Monorail
